import { useState, useCallback } from 'react';
import { getSessionData } from './tokenManager';
import { GOOGLE_AUTH_CONFIG, GoogleUser } from '../config';

export const useGoogleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<GoogleUser | null>(null);

  const generateState = useCallback(() => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }, []);

  const buildAuthUrl = useCallback(() => {
    const state = generateState();
    localStorage.setItem('oauth_state', state);
    
    const params = new URLSearchParams({
      client_id: GOOGLE_AUTH_CONFIG.CLIENT_ID,
      redirect_uri: GOOGLE_AUTH_CONFIG.REDIRECT_URI,
      scope: GOOGLE_AUTH_CONFIG.SCOPES.join(' '),
      response_type: GOOGLE_AUTH_CONFIG.OAUTH_PARAMS.response_type,
      include_granted_scopes: GOOGLE_AUTH_CONFIG.OAUTH_PARAMS.include_granted_scopes,
      state: state,
      prompt: GOOGLE_AUTH_CONFIG.OAUTH_PARAMS.prompt,
      hd: 'psimammoliti.com',
    });

    return `${GOOGLE_AUTH_CONFIG.ENDPOINTS.AUTHORIZATION}?${params.toString()}`;
  }, [generateState]);

  const fetchUserInfo = useCallback(async (accessToken: string): Promise<GoogleUser | null> => {
    try {
      const response = await fetch(`${GOOGLE_AUTH_CONFIG.ENDPOINTS.USERINFO}?access_token=${accessToken}`);
      
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }
      
      const userData = await response.json();
      
      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        given_name: userData.given_name,
        family_name: userData.family_name,
        verified_email: userData.verified_email
      };
    } catch (error) {
      return null;
    }
  }, []);

  const processOAuthCallback = useCallback(async (): Promise<GoogleUser | null> => {
    try {
      const urlParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = urlParams.get('access_token');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      
      if (error) {
        throw new Error(`Error de OAuth: ${error}`);
      }
      
      const savedState = localStorage.getItem('oauth_state');
      if (state !== savedState) {
        throw new Error('Estado OAuth inválido - posible ataque CSRF');
      }
      
      localStorage.removeItem('oauth_state');
      
      if (!accessToken) {
        throw new Error('No se recibió access token');
      }
      
      const userData = await fetchUserInfo(accessToken);
      if (userData) {
        localStorage.setItem('google_user', JSON.stringify(userData));
        localStorage.setItem('google_access_token', accessToken);
        setUser(userData);
        
        window.history.replaceState({}, document.title, window.location.pathname);
        
        return userData;
      }
      
      return null;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      return null;
    }
  }, [fetchUserInfo]);

  const signInWithGoogle = useCallback(async (): Promise<GoogleUser | null> => {
    setIsLoading(true);
    setError(null);

    try {
      if (window.location.hash.includes('access_token')) {
        const userData = await processOAuthCallback();
        setIsLoading(false);
        return userData;
      }

      const authUrl = buildAuthUrl();
      
      const popup = window.open(
        authUrl,
        'google-oauth',
        `width=${GOOGLE_AUTH_CONFIG.POPUP_CONFIG.width},height=${GOOGLE_AUTH_CONFIG.POPUP_CONFIG.height},left=${GOOGLE_AUTH_CONFIG.POPUP_CONFIG.left},top=${GOOGLE_AUTH_CONFIG.POPUP_CONFIG.top},scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        throw new Error('No se pudo abrir la ventana de autenticación. Verifica que los popups estén habilitados.');
      }

      return new Promise((resolve, reject) => {
        let settled = false;

        const processHash = (hash: string) => {
          if (settled) return;
          settled = true;
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          try { popup.close(); } catch (_) { /* empty */ }

          const urlParams = new URLSearchParams(hash.substring(1));
          const accessToken = urlParams.get('access_token');
          const state = urlParams.get('state');
          const oauthError = urlParams.get('error');

          if (oauthError) {
            setIsLoading(false);
            reject(new Error(`Error de OAuth: ${oauthError}`));
            return;
          }

          const savedState = localStorage.getItem('oauth_state');
          if (state !== savedState) {
            setIsLoading(false);
            reject(new Error('Estado OAuth inválido'));
            return;
          }

          localStorage.removeItem('oauth_state');

          if (accessToken) {
            fetchUserInfo(accessToken).then(userData => {
              if (userData) {
                localStorage.setItem('google_user', JSON.stringify(userData));
                localStorage.setItem('google_access_token', accessToken);
                setUser(userData);
                setIsLoading(false);
                resolve(userData);
              } else {
                setIsLoading(false);
                reject(new Error('Error al obtener información del usuario'));
              }
            }).catch(err => {
              setIsLoading(false);
              reject(err);
            });
          } else {
            setIsLoading(false);
            reject(new Error('No se recibió access token'));
          }
        };

        // Listen for postMessage from the popup (primary mechanism)
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'psi-oauth-callback') return;
          processHash(event.data.hash || '');
        };
        window.addEventListener('message', messageHandler);

        // Polling as fallback
        const checkClosed = setInterval(() => {
          try {
            if (popup.closed) {
              if (!settled) {
                settled = true;
                clearInterval(checkClosed);
                window.removeEventListener('message', messageHandler);
                setIsLoading(false);
                reject(new Error('Autenticación cancelada por el usuario'));
              }
              return;
            }

            try {
              const popupUrl = popup.location.href;
              if (popupUrl.includes('access_token') || popupUrl.includes('error=')) {
                const hash = '#' + (popupUrl.split('#')[1] || '');
                processHash(hash);
              }
            } catch (e) {
              // Error de acceso cross-origin - esperado hasta que la redirección ocurra
            }
          } catch (e) {
            // Ignorar errores de acceso al popup
          }
        }, 1000);
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error desconocido');
      setIsLoading(false);
      return null;
    }
  }, [buildAuthUrl, processOAuthCallback, fetchUserInfo]);

  const signOut = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('google_access_token');
      
      if (accessToken) {
        try {
          await fetch(`${GOOGLE_AUTH_CONFIG.ENDPOINTS.REVOKE}?token=${accessToken}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          });
        } catch (error) {
          // No es crítico si falla la revocación
        }
      }
      
      setUser(null);
      localStorage.removeItem('google_user');
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('oauth_state');
      
      return true;
    } catch (error) {
      setError('Error al cerrar sesión');
      return false;
    }
  }, []);

  const checkExistingSession = useCallback(() => {
    try {
      const savedUser = localStorage.getItem('google_user');
      const savedToken = localStorage.getItem('google_access_token');
      const currentSession = getSessionData();
      
      if (savedUser && savedToken) {
        const userData = JSON.parse(savedUser);
        
        // Validar que el usuario de Google corresponda al usuario de la sesión actual (API Core)
        if (currentSession) {
          if (currentSession?.user?.email !== userData.email) {
            // Hay una inconsistencia: el usuario de Google no coincide con la sesión de API Core
            console.warn('⚠️ Usuario de Google no coincide con sesión actual de API Core, limpiando...');
            localStorage.removeItem('google_user');
            localStorage.removeItem('google_access_token');
            localStorage.removeItem('oauth_state');
            return null;
          } 
        }
        
        setUser(userData);
        return userData;
      }
      
      return null;
    } catch (error) {
      localStorage.removeItem('google_user');
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('oauth_state');
      return null;
    }
  }, []);

  return {
    user,
    isLoading,
    error,
    signInWithGoogle,
    signOut,
    checkExistingSession,
    processOAuthCallback,
    clearError: () => setError(null)
  };
};

